import * as React from 'react';
import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { compose } from 'recompose';

import user from 'js/lib/user';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import useDebounce from 'bundles/common/hooks/useDebounce';
import GetS12nCertificateModal from 'bundles/enroll-common/components/common/GetS12nCertificateModal';
import withEnrollment from 'bundles/enroll-common/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import GetS12nIdAndProductVariantBySlug from 'bundles/enroll-common/queries/GetS12nIdAndProductVariantBySlugQuery.graphql';
import type {
  GetS12nIdAndProductVariantBySlugQuery,
  GetS12nIdAndProductVariantBySlugQueryVariables,
  S12nIdAndProductVariantFieldsFragment as Specialization,
} from 'bundles/enroll-common/queries/__generated__/GetS12nIdAndProductVariantBySlugQuery';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/promotions';

import 'css!./__styles__/GetS12nCertificateBanner';

const Y_OFFSET_DEADZONE = 20;
const Y_OFFSET_BREAKPOINT = 70; // arbitrary amount for user scrolling a little bit down
const PARENT_HEIGHT_CLASS = 'with-get-s12n-certificate-banner';

type InputProps = {
  addParentHeightClass: (className: string) => void;
  removeParentHeightClass: (className: string) => void;
  s12nSlug?: string;
};

type PropsFromContainer = {
  s12n: Specialization;
  s12nId: string;
};

type Props = InputProps & PropsFromContainer & PropsFromWithEnrollment;

export const GetS12nCertificateBanner = ({
  s12n,
  enrollmentAvailableChoices,
  addParentHeightClass,
  removeParentHeightClass,
}: Props) => {
  const [didShowModal, setDidShowModal] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const checkScrollPosition = useDebounce(() => {
    const currentOffset = window.pageYOffset;

    // Don't do anything within the range of Y_OFFSET_DEADZONE to Y_OFFSET_BREAKPOINT
    // Since the page height changes when the banner collapses, there is a certain range
    // where the banner can keep flickering as it constantly opens and closes
    if (currentOffset > Y_OFFSET_DEADZONE && currentOffset < Y_OFFSET_BREAKPOINT) {
      return;
    }

    const isHiddenByOffset = window.pageYOffset > Y_OFFSET_BREAKPOINT;
    if (isHiddenByOffset !== isHidden) {
      (isHiddenByOffset ? removeParentHeightClass : addParentHeightClass)(PARENT_HEIGHT_CLASS);
      setIsHidden(isHiddenByOffset);
    }
  }, 200);

  useEffect(() => {
    if (enrollmentAvailableChoices?.hasEarnedS12nCertificate) {
      addParentHeightClass(PARENT_HEIGHT_CLASS);
      window.addEventListener('scroll', checkScrollPosition);
    }
    return () => window.removeEventListener('scroll', checkScrollPosition);
  }, [addParentHeightClass, checkScrollPosition, enrollmentAvailableChoices?.hasEarnedS12nCertificate]);

  const toggleModal = () => {
    setDidShowModal(!didShowModal);
  };

  const getCertificate = () => {
    toggleModal();
  };

  if (!enrollmentAvailableChoices?.hasEarnedS12nCertificate || !s12n) {
    return null;
  }

  const s12nId = s12n.id;
  const trackingData = { s12nId };
  const wrapperClass = classNames('rc-GetS12nCertificateBanner', {
    hidden: isHidden,
  });

  return (
    <TrackedDiv trackingName="get_s12n_certificate_banner_shown" data={trackingData} className={wrapperClass}>
      <div className="details-container horizontal-box align-items-absolute-center">
        <p className="details-text">
          {_t('Good news! These courses were part of another Specialization that you have already completed.')}
        </p>
        <TrackedButton
          trackingName="get_s12n_certificate_banner_get_cert_cta"
          data={trackingData}
          className="cta-button secondary cozy"
          onClick={getCertificate}
        >
          {_t('Get certificate')}
        </TrackedButton>
      </div>
      {didShowModal && <GetS12nCertificateModal s12nId={s12nId} onClose={toggleModal} />}
    </TrackedDiv>
  );
};

const ConnectedGetS12nCertificateBanner = compose<Props, InputProps & PropsFromContainer>(withEnrollment())(
  GetS12nCertificateBanner
);

const GetS12nCertificateBannerHookContainer = (props: InputProps) => {
  const { s12nSlug } = props;

  const { data } = useQuery<GetS12nIdAndProductVariantBySlugQuery, GetS12nIdAndProductVariantBySlugQueryVariables>(
    GetS12nIdAndProductVariantBySlug,
    {
      variables: { slug: `${s12nSlug}` },
      skip: !s12nSlug,
      context: { clientName: 'gatewayGql' },
    }
  );

  const s12n = data?.Specialization?.queryBySlug;
  // This s12nId prop is being used in `withEnrollment`
  const s12nId = s12n?.id;

  if (!s12n || !s12nId) {
    return null;
  }

  return <ConnectedGetS12nCertificateBanner {...props} s12nId={s12nId} s12n={s12n} />;
};

const GetS12nCertificateBannerWithErrorBoundary = (props: InputProps) => {
  if (!props.s12nSlug || !user.isAuthenticatedUser()) {
    return null;
  }

  return (
    <ErrorBoundaryWithTagging componentName="GetS12nCertificateBannerWithErrorBoundary">
      <GetS12nCertificateBannerHookContainer {...props} />
    </ErrorBoundaryWithTagging>
  );
};

export default GetS12nCertificateBannerWithErrorBoundary;
